// Orders list + detail (profile tab, owner panel)
import { apiFetchJSON, getTelegramInitData } from '../../core/api.js';
import { escapeAttr, formatPriceWithRub, formatRub } from '../../core/format.js';
import { getIsOwnerApp } from '../../app/app-state.js';
import { confirmOwnerAction, showOrderPopover } from '../../app/order-popover.js';
import { showView, setActiveTab } from '../../app/navigation.js';
import { closestEl, getEl, getInput, getSelect, getTextArea, isElement } from '../../core/dom.js';
import type { OrderListItem, OrdersApiResponse } from '../../types/api.js';
import { openOrderChat } from './order-chat.js';
import { getTg } from '../../core/telegram.js';

type OrderStatusKey = 'awaiting_payment' | 'payment_pending' | 'approval' | 'building' | 'delivery' | 'ready' | 'cancelled';
type OrderFilter = 'all' | OrderStatusKey;
type OrderSort = 'newest' | 'oldest';

interface OrderStatusUi {
  label: string;
  cls: string;
  info?: string;
}

interface OrdersUiState {
  query: string;
  filter: OrderFilter;
  sort: OrderSort;
}

var ordersCache: OrderListItem[] = [];
var ordersLoading = false;
var ordersLoadSeq = 0;
var ordersUiState: OrdersUiState = {
  query: '',
  filter: 'all',
  sort: 'newest'
};
var ordersOwnerAllCache: OrderListItem[] = [];
var activeOrderUserId: string | null = null;

/** Дата/время создания заказа для UI (без секунд). */
function formatOrderCreatedAt(ms: string | number): string {
  try {
    var d = new Date(Number(ms));
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (_) {
    return '';
  }
}

/** Название в списке заказов (согласовано с сервером orderDisplayTitle). */
function getOrderDisplayTitle(order: OrderListItem | null | undefined): string {
  var rt = order && order.requestType ? String(order.requestType) : '';
  var t = order && order.title ? String(order.title) : '';
  if (rt === 'build' || rt === 'components' || t === 'Список комплектующих') return 'Сборка ПК';
  if (rt === 'upgrade' || t === 'Апгрейд ПК') return 'Апгрейд ПК';
  if (rt === 'checkout' || t === 'Готовые сборки') return 'Готовые сборки';
  return 'Сборка ПК с консультацией';
}

function formatOrdersCount(count: number): string {
  var n = Math.max(0, Math.floor(count));
  var mod10 = n % 10;
  var mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return String(n) + ' заказов';
  if (mod10 === 1) return String(n) + ' заказ';
  if (mod10 >= 2 && mod10 <= 4) return String(n) + ' заказа';
  return String(n) + ' заказов';
}

function normalizeOrderStatus(statusRaw: string | null | undefined): OrderStatusKey {
  var st = String(statusRaw || '').trim().toLowerCase();
  if (!st || st === 'new') return 'approval';
  if (st === 'awaiting_payment' || st === 'await_payment' || st === 'unpaid') return 'awaiting_payment';
  if (st === 'payment_pending' || st === 'payment_claimed' || st === 'pending_payment') return 'payment_pending';
  if (st === 'approval' || st === 'approve' || st === 'review' || st === 'on_approval') return 'approval';
  if (st === 'building' || st === 'assembly' || st === 'build') return 'building';
  if (st === 'delivery' || st === 'shipping' || st === 'in_delivery') return 'delivery';
  if (st === 'ready' || st === 'pickup' || st === 'ready_to_pickup') return 'ready';

  // совместимость
  if (st === 'in_progress' || st === 'inprogress' || st === 'progress' || st === 'processing' || st === 'work') return 'building';
  if (st === 'done' || st === 'completed' || st === 'success') return 'ready';
  if (st === 'cancelled' || st === 'canceled' || st === 'rejected') return 'cancelled';
  return 'approval';
}

function getOrderStatusUi(statusRaw: string | null | undefined): OrderStatusUi {
  var st = normalizeOrderStatus(statusRaw);
  if (st === 'awaiting_payment') {
    return {
      label: 'Ожидает оплаты',
      cls: 'order-status-badge-new',
      info: 'Оплатите по СБП (Ozon банк) — реквизиты на экране оплаты в приложении. После перевода нажмите «Я оплатил».'
    };
  }
  if (st === 'payment_pending') {
    return {
      label: 'Проверка оплаты',
      cls: 'order-status-badge-progress',
      info: 'Мы проверяем поступление перевода. Обычно это занимает немного времени.'
    };
  }
  if (st === 'approval') {
    return {
      label: 'На согласовании',
      cls: 'order-status-badge-new',
      info: 'Проверяем комплектующие и готовим смету'
    };
  }
  if (st === 'building') {
    return {
      label: 'Сборка',
      cls: 'order-status-badge-progress',
      info: 'ПК в сборке. Перед отправкой пришлём видеоотчёт'
    };
  }
  if (st === 'delivery') return { label: 'В доставке', cls: 'order-status-badge-progress', info: 'Заказ в пути' };
  if (st === 'ready') return { label: 'Готов к получению', cls: 'order-status-badge-done', info: 'Заказ готов к получению' };
  if (st === 'cancelled') return { label: 'Отменён', cls: 'order-status-badge-cancelled' };
  return { label: 'На согласовании', cls: 'order-status-badge-new', info: 'Ожидайте согласования с менеджером' };
}

interface OrderCheckoutLine {
  title?: string;
  price?: string;
  qty?: number;
}

interface OrderCheckoutSnapshot {
  lines?: OrderCheckoutLine[];
  totalRub?: number | null;
  deliveryRub?: number | null;
  itemsRub?: number | null;
  phone?: string;
  city?: string;
  firstName?: string;
  lastName?: string;
  deliveryMethod?: string;
  cdekPvzCode?: string;
  pickupPoint?: string;
}

interface OrderShipmentSnapshot {
  trackingNumber?: string;
  cdekOrderUuid?: string;
  shippedAt?: number;
  deliveryPoint?: string;
}

function getOrderCheckout(order: OrderListItem | null | undefined): OrderCheckoutSnapshot | null {
  if (!order || !order.checkout || typeof order.checkout !== 'object') return null;
  return order.checkout as OrderCheckoutSnapshot;
}

function getOrderShipment(order: OrderListItem | null | undefined): OrderShipmentSnapshot | null {
  if (!order || !order.shipment || typeof order.shipment !== 'object') return null;
  return order.shipment as OrderShipmentSnapshot;
}

function isCdekDeliveryOrder(order: OrderListItem | null | undefined): boolean {
  var checkout = getOrderCheckout(order);
  var method = checkout && checkout.deliveryMethod ? String(checkout.deliveryMethod) : 'pickup';
  return method !== 'self-pickup';
}

function cdekTrackingUrl(trackingNumber: string): string {
  return 'https://www.cdek.ru/ru/tracking?order_id=' + encodeURIComponent(trackingNumber);
}

function recipientNameFromCheckout(checkout: OrderCheckoutSnapshot): string {
  var first = checkout.firstName ? String(checkout.firstName).trim() : '';
  var last = checkout.lastName ? String(checkout.lastName).trim() : '';
  var full = [first, last].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (checkout.pickupPoint) return String(checkout.pickupPoint).trim();
  return 'Получатель';
}

function isCustomBuildOrder(order: OrderListItem | null | undefined): boolean {
  var rt = order && order.requestType ? String(order.requestType) : '';
  return rt === 'build' || rt === 'components' || rt === 'upgrade';
}

const ORDER_FLOW_STEPS = [
  { key: 'request', title: 'Заявка', text: 'Вы отправили список и контакты' },
  { key: 'approval', title: 'Согласование', text: 'Проверяем наличие и готовим сумму' },
  { key: 'pay', title: 'Оплата', text: 'Оплата 100% после согласования сметы' },
  { key: 'building', title: 'Сборка и тест', text: 'Соберём ПК и пришлём видеоотчёт' },
  { key: 'delivery', title: 'Отправка', text: 'Упаковка и передача в СДЭК' },
  { key: 'ready', title: 'Готово', text: 'Заказ завершён' }
] as const;

function getOrderFlowStepState(
  st: OrderStatusKey,
  stepKey: string,
  hasVideoReport?: boolean
): 'done' | 'active' | '' {
  if (st === 'cancelled') return '';
  if (stepKey === 'request') return 'done';
  if (stepKey === 'approval') {
    if (st === 'approval') return 'active';
    if (
      st === 'awaiting_payment' ||
      st === 'payment_pending' ||
      st === 'building' ||
      st === 'delivery' ||
      st === 'ready'
    ) {
      return 'done';
    }
  }
  if (stepKey === 'pay') {
    if (st === 'awaiting_payment' || st === 'payment_pending') return 'active';
    if (st === 'building' || st === 'delivery' || st === 'ready') return 'done';
  }
  if (stepKey === 'building') {
    if (st === 'building') return hasVideoReport ? 'done' : 'active';
    if (st === 'delivery' || st === 'ready') return 'done';
  }
  if (stepKey === 'delivery') {
    if (st === 'delivery') return 'active';
    if (st === 'ready') return 'done';
  }
  if (stepKey === 'ready' && st === 'ready') return 'active';
  return '';
}

function getOrderActiveFlowStep(order: OrderListItem | null | undefined): {
  title: string;
  text: string;
} {
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  if (st === 'cancelled') {
    return { title: 'Заказ отменён', text: 'Если это ошибка — напишите нам в чат' };
  }
  for (var i = 0; i < ORDER_FLOW_STEPS.length; i++) {
    var step = ORDER_FLOW_STEPS[i];
    if (getOrderFlowStepState(st, step.key) === 'active') {
      return { title: step.title, text: step.text };
    }
  }
  if (st === 'ready') {
    var last = ORDER_FLOW_STEPS[ORDER_FLOW_STEPS.length - 1];
    return { title: last.title, text: last.text };
  }
  return { title: ORDER_FLOW_STEPS[1].title, text: ORDER_FLOW_STEPS[1].text };
}

function getOrderSpotlightTheme(order: OrderListItem | null | undefined): string {
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  if (st === 'awaiting_payment' || st === 'payment_pending') return 'payment';
  if (st === 'building' || st === 'delivery') return 'progress';
  if (st === 'ready') return 'done';
  if (st === 'cancelled') return 'cancelled';
  return 'approval';
}

function renderOrderSpotlightProgress(order: OrderListItem): string {
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  if (!isCustomBuildOrder(order) || st === 'cancelled') return '';
  var progress = getOrderProgress(order);
  if (progress.step <= 0) return '';
  return (
    '<div class="order-spotlight-progress">' +
    '<div class="order-spotlight-progress-track">' +
    '<div class="order-spotlight-progress-fill" style="width:' +
    String(progress.pct) +
    '%"></div></div>' +
    '<span class="order-spotlight-progress-label">' +
    escapeAttr(progress.label) +
    '</span></div>'
  );
}

function getOrderSpotlightHint(order: OrderListItem): string {
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  if (st === 'approval') return 'Проверяем наличие и готовим смету';
  if (st === 'payment_pending') return 'Проверяем поступление перевода';
  if (st === 'building') {
    var hasVideo = !!(order && order.videoReport && order.videoReport.sentAt);
    return hasVideo ? 'Видеоотчёт отправлен' : 'Перед отправкой пришлём видеоотчёт';
  }
  if (st === 'delivery') return 'Заказ в пути — трек в карточке заказа';
  if (st === 'ready') return 'Можно забирать';
  return getOrderActiveFlowStep(order).text;
}

function renderOrderSpotlightAccent(order: OrderListItem): string {
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  if (st === 'awaiting_payment') {
    var ck = getOrderCheckout(order);
    var totalRub = ck && ck.totalRub != null ? Number(ck.totalRub) : null;
    if (totalRub != null && totalRub > 0) {
      return (
        '<p class="order-spotlight-accent">' +
        '<span class="order-spotlight-sum">' +
        escapeAttr(formatRub(totalRub)) +
        '</span>' +
        '<span class="order-spotlight-sum-note">к оплате</span></p>'
      );
    }
    return '<p class="order-spotlight-accent order-spotlight-accent--muted">Откройте заказ, чтобы увидеть смету</p>';
  }
  if (st === 'cancelled') {
    return '<p class="order-spotlight-accent order-spotlight-accent--muted">Заказ отменён</p>';
  }
  var hint = getOrderSpotlightHint(order);
  if (!hint) return '';
  return '<p class="order-spotlight-accent">' + escapeAttr(hint) + '</p>';
}

function orderCardDataAttrs(order: OrderListItem): string {
  return (
    ' data-order-id="' +
    escapeAttr(order && order.id ? String(order.id) : '') +
    '" data-order-user-id="' +
    escapeAttr(order && order.userId ? String(order.userId) : '') +
    '"'
  );
}

function renderOrderSpotlight(order: OrderListItem): string {
  var title = escapeAttr(getOrderDisplayTitle(order));
  var createdAt = order && order.createdAt ? formatOrderCreatedAt(order.createdAt) : '';
  var status = getOrderStatusUi(order && order.status ? String(order.status) : '');
  var theme = getOrderSpotlightTheme(order);
  var progress = renderOrderSpotlightProgress(order);
  var accent = renderOrderSpotlightAccent(order);
  var statusInfo =
    !getIsOwnerApp() && status && status.info
      ? '<button type="button" class="order-info-btn" aria-label="Информация" data-info="' +
        escapeAttr(status.info) +
        '">i</button>'
      : '';

  return (
    '<article class="order-spotlight order-spotlight--' +
    escapeAttr(theme) +
    '" role="button" tabindex="0"' +
    orderCardDataAttrs(order) +
    '">' +
    '<div class="order-spotlight-head">' +
    '<span class="order-status-wrap">' +
    '<span class="order-status-badge ' +
    escapeAttr(status.cls) +
    '">' +
    escapeAttr(status.label) +
    '</span>' +
    statusInfo +
    '</span>' +
    '<span class="order-spotlight-chevron" aria-hidden="true">›</span>' +
    '</div>' +
    '<h2 class="order-spotlight-title">' +
    title +
    '</h2>' +
    (progress || accent
      ? '<div class="order-spotlight-body">' + progress + accent + '</div>'
      : '') +
    (createdAt
      ? '<footer class="order-spotlight-foot"><time class="order-spotlight-date">' +
        escapeAttr(createdAt) +
        '</time></footer>'
      : '') +
    '</article>'
  );
}

function renderOrderRowCompact(order: OrderListItem): string {
  var title = escapeAttr(getOrderDisplayTitle(order));
  var createdAt = order && order.createdAt ? formatOrderCreatedAt(order.createdAt) : '';
  var status = getOrderStatusUi(order && order.status ? String(order.status) : '');
  return (
    '<article class="order-row-compact" role="button" tabindex="0"' +
    orderCardDataAttrs(order) +
    '">' +
    '<div class="order-row-compact-main">' +
    '<span class="order-row-compact-title">' +
    title +
    '</span>' +
    '<span class="order-status-badge ' +
    escapeAttr(status.cls) +
    '">' +
    escapeAttr(status.label) +
    '</span>' +
    '</div>' +
    (createdAt ? '<time class="order-row-compact-date">' + escapeAttr(createdAt) + '</time>' : '') +
    '<span class="order-row-compact-chevron" aria-hidden="true">›</span>' +
    '</article>'
  );
}

function renderOrderFlowTimeline(order: OrderListItem): void {
  var wrap = getEl('order-flow-wrap');
  var flowSection = getEl('order-detail-flow-section');
  if (!wrap) return;
  if (!isCustomBuildOrder(order)) {
    wrap.innerHTML = '';
    if (flowSection) flowSection.hidden = true;
    return;
  }
  if (flowSection) flowSection.hidden = false;
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  var hasVideo = !!(order && order.videoReport && order.videoReport.sentAt);
  wrap.innerHTML =
    '<ol class="order-flow-timeline">' +
    ORDER_FLOW_STEPS.map(function (step, i) {
      var state = getOrderFlowStepState(st, step.key, hasVideo);
      var cls = 'order-flow-step' + (state ? ' order-flow-step--' + state : '');
      return (
        '<li class="' +
        escapeAttr(cls) +
        '" style="animation-delay:' +
        (i * 0.05).toFixed(2) +
        's">' +
        '<div class="order-flow-dot">' +
        (state === 'done' ? '✓' : String(i + 1)) +
        '</div>' +
        '<div class="order-flow-step-body">' +
        '<p class="order-flow-step-title">' +
        escapeAttr(step.title) +
        '</p>' +
        '<p class="order-flow-step-text">' +
        escapeAttr(step.text) +
        '</p>' +
        '</div></li>'
      );
    }).join('') +
    '</ol>';
}

function renderOrderQuoteBlock(order: OrderListItem): void {
  var block = getEl('order-quote-block');
  var linesEl = getEl('order-quote-lines');
  var totalEl = getEl('order-quote-total-value');
  var payBtnCustomer = getEl<HTMLButtonElement>('order-pay-btn');
  if (!block || !linesEl || !totalEl) return;

  var checkout = getOrderCheckout(order);
  var lines = checkout && Array.isArray(checkout.lines) ? checkout.lines : [];
  var totalRub = checkout && checkout.totalRub != null ? Number(checkout.totalRub) : null;
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  var showQuote = lines.length > 0 && totalRub != null && totalRub > 0;

  if (!showQuote) {
    block.hidden = true;
    if (payBtnCustomer) payBtnCustomer.hidden = true;
    return;
  }

  block.hidden = false;
  linesEl.innerHTML = lines
    .map(function (line) {
      var title = line && line.title ? String(line.title) : '';
      var price = line && line.price ? formatPriceWithRub(String(line.price)) || '—' : '—';
      var qty = line && line.qty ? Number(line.qty) : 1;
      var qtyLabel = qty > 1 ? ' × ' + qty : '';
      return (
        '<div class="order-quote-line">' +
        '<span class="order-quote-line-title">' +
        escapeAttr(title + qtyLabel) +
        '</span>' +
        '<span class="order-quote-line-price">' +
        escapeAttr(price) +
        '</span></div>'
      );
    })
    .join('');
  totalEl.textContent = formatRub(totalRub!);

  if (payBtnCustomer) {
    var canPay = !getIsOwnerApp() && (st === 'awaiting_payment' || st === 'payment_pending');
    payBtnCustomer.hidden = !canPay;
    payBtnCustomer.onclick = function () {
      var oid = order && order.id ? String(order.id) : '';
      if (!oid) return;
      void import('../checkout/ui.js').then(function (m) {
        return m.openOrderPaymentForOrder(oid);
      });
    };
  }
}

var ownerQuoteRowSeed = 0;

function renderOwnerQuotePanel(order: OrderListItem): void {
  var panel = getEl('order-owner-quote');
  if (!panel) return;
  var show =
    getIsOwnerApp() &&
    isCustomBuildOrder(order) &&
    normalizeOrderStatus(order && order.status ? String(order.status) : '') === 'approval';
  panel.hidden = !show;
  if (!show) return;

  var rowsEl = getEl('order-quote-rows');
  var addBtn = getEl<HTMLButtonElement>('order-quote-add-row');
  var sendBtn = getEl<HTMLButtonElement>('order-quote-send-btn');
  var assemblyEl = getInput('order-quote-assembly');
  var deliveryEl = getInput('order-quote-delivery');
  if (!rowsEl || !sendBtn) return;

  function addQuoteRow(title?: string, price?: string): void {
    ownerQuoteRowSeed += 1;
    var id = 'qrow-' + ownerQuoteRowSeed;
    var row = document.createElement('div');
    row.className = 'order-quote-row';
    row.dataset.rowId = id;
    row.innerHTML =
      '<input class="form-input" data-quote-title placeholder="Позиция" value="' +
      escapeAttr(title || '') +
      '" />' +
      '<input class="form-input" data-quote-price inputmode="numeric" placeholder="₽" value="' +
      escapeAttr(price || '') +
      '" />' +
      '<button type="button" class="order-quote-row-remove" aria-label="Удалить">×</button>';
    rowsEl!.appendChild(row);
    row.querySelector('.order-quote-row-remove')?.addEventListener('click', () => {
      row.remove();
    });
  }

  if (rowsEl.dataset.bound !== '1') {
    rowsEl.dataset.bound = '1';
    addBtn?.addEventListener('click', () => addQuoteRow());
  }

  if (rowsEl.dataset.orderId !== String(order.id || '')) {
    rowsEl.dataset.orderId = String(order.id || '');
    rowsEl.innerHTML = '';
    addQuoteRow('CPU', '');
    addQuoteRow('GPU', '');
    addQuoteRow('Остальные комплектующие', '');
    if (assemblyEl) assemblyEl.value = '1500';
    if (deliveryEl) {
      var ck = getOrderCheckout(order);
      deliveryEl.value =
        ck && ck.deliveryRub != null && Number.isFinite(Number(ck.deliveryRub))
          ? String(ck.deliveryRub)
          : '';
    }
  }

  sendBtn.onclick = async function () {
    var quoteSendBtn = sendBtn;
    if (!quoteSendBtn) return;
    var uid = activeOrderUserId || (order && order.userId ? String(order.userId) : '');
    var oid = order && order.id ? String(order.id) : '';
    if (!uid || !oid) return;
    var rowEls = rowsEl!.querySelectorAll('.order-quote-row');
    var lines: { title: string; price: string; qty: number }[] = [];
    rowEls.forEach(function (row) {
      var titleIn = row.querySelector('[data-quote-title]') as HTMLInputElement | null;
      var priceIn = row.querySelector('[data-quote-price]') as HTMLInputElement | null;
      var title = titleIn ? String(titleIn.value || '').trim() : '';
      var price = priceIn ? String(priceIn.value || '').trim() : '';
      if (!title) return;
      lines.push({ title, price, qty: 1 });
    });
    if (!lines.length) return;
    quoteSendBtn.disabled = true;
    quoteSendBtn.textContent = 'Отправка...';
    try {
      var data = await apiFetchJSON<OrdersApiResponse>('/api/owner/orders/set-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: uid,
          orderId: oid,
          lines,
          assemblyRub: assemblyEl ? assemblyEl.value : '0',
          deliveryRub: deliveryEl ? deliveryEl.value : '0'
        })
      });
      if (data?.ok && data.item) {
        renderOrderDetail(data.item as OrderListItem);
        renderOrdersListState();
      }
    } catch {
      /* ignore */
    }
    quoteSendBtn.disabled = false;
    quoteSendBtn.textContent = 'Отправить смету клиенту';
  };
}

function orderDetailShortId(orderId: string): string {
  var id = String(orderId || '').trim();
  if (!id) return '';
  var tail = id.split('-').pop();
  return tail && tail.length <= 12 ? tail : id.slice(-8);
}

function readVideoFileAsBase64(file: File): Promise<{ base64: string; mime: string }> {
  return new Promise(function (resolve, reject) {
    var reader = new FileReader();
    reader.onload = function () {
      var result = String(reader.result || '');
      var idx = result.indexOf(',');
      resolve({
        base64: idx >= 0 ? result.slice(idx + 1) : result,
        mime: String(file.type || 'video/mp4')
      });
    };
    reader.onerror = function () {
      reject(reader.error || new Error('read_failed'));
    };
    reader.readAsDataURL(file);
  });
}

function formatVideoSentAt(ms: number | string | undefined): string {
  if (!ms) return '';
  try {
    var d = new Date(Number(ms));
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (_) {
    return '';
  }
}

function renderOrderVideoReportSection(order: OrderListItem): void {
  var section = getEl('order-video-report-section');
  var pendingSection = getEl('order-video-pending-section');
  var card = getEl('order-video-report-card');
  if (!section || !card) return;

  var report = order && order.videoReport ? order.videoReport : null;
  var sentAt = report && report.sentAt ? Number(report.sentAt) : 0;
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');

  if (!sentAt) {
    section.hidden = true;
    card.innerHTML = '';
    if (pendingSection) {
      var showPending =
        !getIsOwnerApp() &&
        st === 'building' &&
        (isCustomBuildOrder(order) || String(order.requestType || '') === 'checkout');
      pendingSection.hidden = !showPending;
    }
    return;
  }

  if (pendingSection) pendingSection.hidden = true;
  section.hidden = false;
  var when = formatVideoSentAt(sentAt);
  var checklist =
    report && report.checklist ? String(report.checklist).trim() : '';
  var html =
    '<div class="order-video-report-head">' +
    '<span class="order-video-report-icon" aria-hidden="true">▶</span>' +
    '<div><p class="order-video-report-status">Видеоотчёт готов</p>' +
    (when ? '<p class="order-video-report-date">' + escapeAttr(when) + '</p>' : '') +
    '</div></div>';
  if (checklist) {
    var lines = checklist.split(/\r?\n/).map(function (l) { return String(l || '').trim(); }).filter(Boolean);
    html +=
      '<div class="order-video-report-checklist"><p class="order-video-report-checklist-title">Проверено</p><ul class="order-video-report-checklist-list">' +
      lines
        .map(function (line) {
          return '<li class="order-video-report-checklist-item">' + escapeAttr(line) + '</li>';
        })
        .join('') +
      '</ul></div>';
  }
  html +=
    '<p class="order-video-report-note">Видео отправлено в личные сообщения Telegram-бота.</p>' +
    '<button type="button" class="submit-btn submit-btn--secondary order-video-open-bot-btn" id="order-video-open-bot">Открыть в Telegram</button>';
  card.innerHTML = html;

  var openBtn = getEl<HTMLButtonElement>('order-video-open-bot');
  if (openBtn) {
    openBtn.onclick = function () {
      var tg = getTg();
      if (tg && typeof tg.openTelegramLink === 'function') {
        try {
          tg.openTelegramLink('https://t.me/DeepDesignPC_bot');
        } catch (_) {}
      }
    };
  }
}

function renderOrderSupportActions(order: OrderListItem): void {
  var section = getEl('order-support-section');
  var askBtn = getEl<HTMLButtonElement>('order-ask-btn');
  if (!section || !askBtn) return;

  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  var show =
    !getIsOwnerApp() &&
    st !== 'cancelled' &&
    (st === 'building' || st === 'delivery' || st === 'ready' || st === 'approval' || st === 'awaiting_payment' || st === 'payment_pending');
  section.hidden = !show;
  if (!show) return;

  askBtn.onclick = function () {
    var oid = order && order.id ? String(order.id) : '';
    if (!oid) return;
    openOrderChat({
      orderId: oid,
      orderTitle: getOrderDisplayTitle(order),
      backTo: 'view-order-detail'
    });
  };
}

function renderOrderShipmentSection(order: OrderListItem): void {
  var section = getEl('order-shipment-section');
  var card = getEl('order-shipment-card');
  if (!section || !card) return;

  var shipment = getOrderShipment(order);
  var tracking = shipment && shipment.trackingNumber ? String(shipment.trackingNumber).trim() : '';
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  var show =
    !getIsOwnerApp() &&
    isCdekDeliveryOrder(order) &&
    (tracking || st === 'delivery' || st === 'ready');

  if (!show) {
    section.hidden = true;
    card.innerHTML = '';
    return;
  }

  section.hidden = false;
  var when =
    shipment && shipment.shippedAt ? formatVideoSentAt(shipment.shippedAt) : '';
  var checkout = getOrderCheckout(order);
  var pvz = shipment && shipment.deliveryPoint
    ? String(shipment.deliveryPoint)
    : checkout && checkout.cdekPvzCode
      ? String(checkout.cdekPvzCode)
      : '';

  var html = '';
  if (tracking) {
    html +=
      '<p class="order-shipment-track">Трек: ' + escapeAttr(tracking) + '</p>' +
      '<button type="button" class="submit-btn submit-btn--secondary order-shipment-track-btn" id="order-shipment-track-btn">Отследить на cdek.ru</button>';
  } else {
    html += '<p class="order-shipment-track">Отправление создано</p>';
    html +=
      '<p class="order-shipment-meta">Трек-номер появится в течение нескольких минут. Мы пришлём уведомление в Telegram.</p>';
  }
  if (when) {
    html += '<p class="order-shipment-meta">Отправлено ' + escapeAttr(when) + '</p>';
  }
  if (pvz) {
    html += '<p class="order-shipment-meta">ПВЗ: ' + escapeAttr(pvz) + '</p>';
  }
  card.innerHTML = html;

  if (tracking) {
    var trackBtn = getEl<HTMLButtonElement>('order-shipment-track-btn');
    if (trackBtn) {
      trackBtn.onclick = function () {
        var url = cdekTrackingUrl(tracking);
        var tg = getTg();
        if (tg && typeof tg.openLink === 'function') {
          try {
            tg.openLink(url);
            return;
          } catch (_) {}
        }
        try {
          window.open(url, '_blank');
        } catch (_) {}
      };
    }
  }
}

function renderOrderOwnerShipmentPanel(order: OrderListItem): void {
  var panel = getEl('order-owner-shipment');
  var divider = getEl('order-owner-shipment-divider');
  var summaryEl = getEl('order-shipment-summary');
  var createBtn = getEl<HTMLButtonElement>('order-shipment-create-btn');
  var hintEl = getEl('order-shipment-create-hint');
  if (!panel || !summaryEl || !createBtn) return;
  const createShipmentBtn = createBtn;

  var showOwner = getIsOwnerApp() && !!(order && order.userId);
  var shipment = getOrderShipment(order);
  var hasShipment = !!(shipment && (shipment.trackingNumber || shipment.cdekOrderUuid));
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  var showPanel =
    showOwner &&
    isCdekDeliveryOrder(order) &&
    !hasShipment &&
    (st === 'building' || st === 'delivery' || st === 'ready');

  panel.hidden = !showPanel;
  if (divider) divider.hidden = !showPanel;
  if (!showPanel) {
    if (hintEl) {
      hintEl.style.display = 'none';
      hintEl.textContent = '';
    }
    return;
  }

  var checkout = getOrderCheckout(order);
  if (!checkout) {
    summaryEl.innerHTML = '<p class="order-shipment-meta">Нет данных checkout</p>';
    createShipmentBtn.disabled = true;
    return;
  }

  var rows = [
    ['Получатель', recipientNameFromCheckout(checkout)],
    ['Телефон', checkout.phone ? String(checkout.phone) : '—'],
    ['ПВЗ', checkout.cdekPvzCode ? String(checkout.cdekPvzCode) : '—'],
    ['Город', checkout.city ? String(checkout.city) : '—']
  ];
  summaryEl.innerHTML = rows
    .map(function (row) {
      return (
        '<div class="order-shipment-summary-row"><strong>' +
        escapeAttr(row[0]) +
        ':</strong> ' +
        escapeAttr(row[1]) +
        '</div>'
      );
    })
    .join('');

  createShipmentBtn.disabled = false;
  createShipmentBtn.onclick = async function () {
    var uid = activeOrderUserId || (order && order.userId ? String(order.userId) : '');
    var oid = order && order.id ? String(order.id) : '';
    if (!uid || !oid) return;

    createShipmentBtn.disabled = true;
    createShipmentBtn.textContent = 'Создание...';
    if (hintEl) {
      hintEl.style.display = 'none';
      hintEl.textContent = '';
    }
    try {
      var data = await apiFetchJSON<OrdersApiResponse & { trackingUrl?: string | null }>(
        '/api/owner/orders/create-shipment',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: uid, orderId: oid })
        }
      );
      if (data?.ok && data.item) {
        renderOrderDetail(data.item as OrderListItem);
        renderOrdersListState();
        if (hintEl) {
          hintEl.style.display = '';
          hintEl.textContent = data.trackingUrl
            ? 'Отправление создано, трек отправлен клиенту.'
            : 'Отправление создано. Трек появится через несколько минут.';
        }
      } else if (hintEl) {
        hintEl.style.display = '';
        hintEl.textContent =
          data && data.error ? String(data.error) : 'Не удалось создать отправление.';
      }
    } catch (_) {
      if (hintEl) {
        hintEl.style.display = '';
        hintEl.textContent = 'Ошибка сети при создании отправления.';
      }
    }
    createShipmentBtn.disabled = false;
    createShipmentBtn.textContent = 'Создать отправление СДЭК';
  };
}

function renderOrderOwnerFulfillment(order: OrderListItem): void {
  var section = getEl('order-owner-fulfillment');
  var videoPanel = getEl('order-owner-video');
  var divider = getEl('order-owner-fulfillment-divider');
  var chatBtn = getEl<HTMLButtonElement>('order-owner-open-chat');
  if (!section || !videoPanel || !chatBtn) return;

  var showOwner = getIsOwnerApp() && !!(order && order.userId);
  section.hidden = !showOwner;
  if (!showOwner) {
    ownerVideoPendingFile = null;
    return;
  }

  renderOrderOwnerVideoPanel(order, videoPanel, divider);
  renderOrderOwnerShipmentPanel(order);

  chatBtn.onclick = function () {
    var uid = order && order.userId ? String(order.userId) : activeOrderUserId || '';
    if (!uid) return;
    var u1 = order && order.username_tg ? String(order.username_tg) : '';
    var u2 = order && order.username_form ? String(order.username_form) : '';
    var username = (u1 && u1.trim()) || (u2 && u2.trim()) || '';
    openOrderChat({
      orderId: order && order.id ? String(order.id) : '',
      orderTitle: getOrderDisplayTitle(order),
      userId: uid,
      username: username,
      backTo: 'view-order-detail'
    });
  };
}

var ownerVideoPendingFile: File | null = null;
var ownerVideoBoundOrderId: string | null = null;

function renderOrderOwnerVideoPanel(
  order: OrderListItem,
  panel: HTMLElement,
  dividerEl: HTMLElement | null
): void {

  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  var hasVideo = !!(order && order.videoReport && order.videoReport.sentAt);
  var showVideo =
    !hasVideo && (st === 'building' || st === 'delivery' || st === 'ready');
  panel.hidden = !showVideo;
  if (dividerEl) dividerEl.hidden = !showVideo;

  var orderId = order && order.id ? String(order.id) : '';
  if (orderId && ownerVideoBoundOrderId !== orderId) {
    ownerVideoBoundOrderId = orderId;
    ownerVideoPendingFile = null;
  }

  if (!showVideo) {
    ownerVideoPendingFile = null;
    return;
  }

  var checklistEl = getTextArea('order-video-checklist');
  var fileInput = getInput('order-video-file');
  var pickBtn = getEl<HTMLButtonElement>('order-video-pick-btn');
  var fileNameEl = getEl('order-video-file-name');
  var sendBtn = getEl<HTMLButtonElement>('order-video-send-btn');
  var hintEl = getEl('order-video-send-hint');
  if (!sendBtn) return;
  const videoSendBtn = sendBtn;

  if (checklistEl && checklistEl.dataset.orderId !== orderId) {
    checklistEl.dataset.orderId = orderId;
    checklistEl.value = 'POST, Windows, стресс-тест ~15 мин';
  }

  if (fileInput && pickBtn && pickBtn.dataset.bound !== '1') {
    const videoFileInput = fileInput;
    pickBtn.dataset.bound = '1';
    pickBtn.addEventListener('click', function () {
      try { videoFileInput.value = ''; } catch (_) {}
      try { videoFileInput.click(); } catch (_) {}
    });
    videoFileInput.addEventListener('change', function () {
      var file = videoFileInput.files && videoFileInput.files[0] ? videoFileInput.files[0] : null;
      ownerVideoPendingFile = file;
      if (fileNameEl) {
        if (file) {
          fileNameEl.hidden = false;
          var mb = file.size / 1024 / 1024;
          fileNameEl.textContent =
            file.name + ' · ' + (mb >= 0.1 ? mb.toFixed(1) : '0.1') + ' МБ';
        } else {
          fileNameEl.hidden = true;
          fileNameEl.textContent = '';
        }
      }
    });
  }

  if (fileNameEl && !ownerVideoPendingFile) {
    fileNameEl.hidden = true;
    fileNameEl.textContent = '';
  }

  videoSendBtn.onclick = async function () {
    var uid = activeOrderUserId || (order && order.userId ? String(order.userId) : '');
    var oid = order && order.id ? String(order.id) : '';
    if (!uid || !oid) return;
    if (!ownerVideoPendingFile) {
      if (hintEl) {
        hintEl.style.display = '';
        hintEl.textContent = 'Выберите видеофайл.';
      }
      return;
    }
    if (ownerVideoPendingFile.size > 20 * 1024 * 1024) {
      if (hintEl) {
        hintEl.style.display = '';
        hintEl.textContent = 'Файл больше 20 МБ.';
      }
      return;
    }

    videoSendBtn.disabled = true;
    videoSendBtn.textContent = 'Отправка...';
    if (hintEl) {
      hintEl.style.display = 'none';
      hintEl.textContent = '';
    }
    try {
      var packed = await readVideoFileAsBase64(ownerVideoPendingFile);
      var data = await apiFetchJSON<OrdersApiResponse & { botLink?: string }>(
        '/api/owner/orders/send-video-report',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: uid,
            orderId: oid,
            videoBase64: packed.base64,
            mime: packed.mime,
            checklist: checklistEl ? checklistEl.value : ''
          })
        }
      );
      if (data?.ok && data.item) {
        ownerVideoPendingFile = null;
        if (fileInput) fileInput.value = '';
        if (fileNameEl) {
          fileNameEl.hidden = true;
          fileNameEl.textContent = '';
        }
        renderOrderDetail(data.item as OrderListItem);
        renderOrdersListState();
        if (hintEl) {
          hintEl.style.display = '';
          hintEl.textContent = 'Видеоотчёт отправлен клиенту.';
        }
      } else {
        if (hintEl) {
          hintEl.style.display = '';
          hintEl.textContent =
            data && data.error ? String(data.error) : 'Не удалось отправить видео.';
        }
      }
    } catch (_) {
      if (hintEl) {
        hintEl.style.display = '';
        hintEl.textContent = 'Ошибка сети при отправке видео.';
      }
    }
    videoSendBtn.disabled = false;
    videoSendBtn.textContent = 'Отправить видеоотчёт';
  };
}

function getOrderTypeKey(order: OrderListItem | null | undefined): string {
  var rt = order && order.requestType ? String(order.requestType) : '';
  if (rt === 'checkout') return 'catalog';
  if (rt === 'upgrade') return 'upgrade';
  if (rt === 'components') return 'components';
  return 'build';
}

function getOrderTypeIcon(typeKey: string): string {
  if (typeKey === 'catalog') return '📦';
  if (typeKey === 'upgrade') return '⚡';
  if (typeKey === 'components') return '🛠';
  return '💬';
}

function getOrderProgress(order: OrderListItem | null | undefined): { step: number; total: number; pct: number; label: string } {
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  var step = 1;
  if (st === 'approval') step = 2;
  else if (st === 'awaiting_payment' || st === 'payment_pending') step = 3;
  else if (st === 'building') step = 4;
  else if (st === 'delivery') step = 5;
  else if (st === 'ready') step = 6;
  else if (st === 'cancelled') step = 0;
  var total = 6;
  var pct = step > 0 ? Math.round((step / total) * 100) : 0;
  return {
    step: step,
    total: total,
    pct: pct,
    label: step > 0 ? 'Этап ' + step + ' из ' + total : 'Отменён'
  };
}

function renderOrderCardSpecs(preview: string): string {
  var lines = String(preview || '')
    .split(/\r?\n/)
    .map(function (l: string) { return String(l || '').trim(); })
    .filter(Boolean)
    .slice(0, 2);
  if (!lines.length) return '';
  return (
    '<div class="order-card-specs">' +
    lines
      .map(function (line: string) {
        return '<span class="order-card-spec">' + escapeAttr(line) + '</span>';
      })
      .join('') +
    '</div>'
  );
}

function renderOrderTasksHtml(order: OrderListItem | null | undefined): string {
  var tasks = order && order.tasks ? String(order.tasks) : '';
  if (!tasks.trim()) {
    return '<p class="order-detail-tasks-empty">Описание заказа пока не заполнено.</p>';
  }
  var lines = tasks.split(/\r?\n/).map(function (l: string) { return String(l || '').trim(); }).filter(Boolean);
  return (
    '<ul class="order-detail-tasks-list">' +
    lines
      .map(function (line: string) {
        return (
          '<li class="order-detail-task-item">' +
          '<span class="order-detail-task-bullet" aria-hidden="true"></span>' +
          '<span>' +
          escapeAttr(line) +
          '</span></li>'
        );
      })
      .join('') +
    '</ul>'
  );
}

function applyOrderDetailHeroTheme(order: OrderListItem | null | undefined): void {
  var hero = getEl('order-detail-hero');
  if (!hero) return;
  var st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  hero.className = 'order-detail-hero';
  if (st === 'awaiting_payment' || st === 'payment_pending') hero.classList.add('order-detail-hero--payment');
  else if (st === 'building' || st === 'delivery') hero.classList.add('order-detail-hero--progress');
  else if (st === 'ready') hero.classList.add('order-detail-hero--done');
  else if (st === 'cancelled') hero.classList.add('order-detail-hero--cancelled');
}

function getOrderSubtitle(order: OrderListItem | null | undefined): string {
  var rt = order && order.requestType ? String(order.requestType) : '';
  if (rt === 'components') return 'Проверка списка комплектующих';
  if (rt === 'upgrade') return 'Запрос на апгрейд';
  if (rt === 'checkout') return 'Оформление заказа';
  return 'Сборка под задачи и бюджет';
}

function getOrderTasksPreview(order: OrderListItem | null | undefined): string {
  var tasks = order && order.tasks ? String(order.tasks) : '';
  if (!tasks) return 'Описание заказа пока не заполнено.';

  // Для пользователя в списке заказов показываем только CPU + GPU (2 строки) и "...".
  // Ожидаемый формат: "CPU: ...\nGPU: ...\n..."
  if (!getIsOwnerApp()) {
    try {
      var lines = tasks.split(/\r?\n/).map(function (l: string) { return String(l || '').trim(); }).filter(Boolean);
      var cpu = '';
      var gpu = '';
      for (var i = 0; i < lines.length; i++) {
        var low = lines[i].toLowerCase();
        if (!cpu && (low.indexOf('cpu:') === 0 || low.indexOf('процессор:') === 0 || low.indexOf('cpu ') === 0)) cpu = lines[i];
        if (!gpu && (low.indexOf('gpu:') === 0 || low.indexOf('видеокарта:') === 0 || low.indexOf('gpu ') === 0)) gpu = lines[i];
        if (cpu && gpu) break;
      }
      // Если не нашли по ключам — берём первые две осмысленные строки
      if (!cpu && lines[0]) cpu = lines[0];
      if (!gpu && lines[1]) gpu = lines[1];

      if (cpu && gpu) return cpu + '\n' + gpu + ' ...';
      if (cpu) return cpu + ' ...';
      if (gpu) return gpu + ' ...';
    } catch (_) {}
  }

  var compact = tasks.replace(/\s+/g, ' ').trim();
  return compact.length > 120 ? compact.slice(0, 120).trim() + '...' : compact;
}

function computeOrdersSummary(items: OrderListItem[]): string {
  var total = Array.isArray(items) ? items.length : 0;
  var approval = 0;
  var building = 0;
  var delivery = 0;
  var ready = 0;
  for (var i = 0; i < total; i++) {
    var st = normalizeOrderStatus(items[i] && items[i].status ? String(items[i].status) : '');
    if (st === 'approval') approval++;
    else if (st === 'building') building++;
    else if (st === 'delivery') delivery++;
    else if (st === 'ready') ready++;
  }
  return 'Всего: ' + total + ' · На согласовании: ' + approval + ' · Сборка: ' + building + ' · В доставке: ' + delivery + ' · Готово: ' + ready;
}

function matchesOrderFilter(order: OrderListItem, filter: OrderFilter): boolean {
  if (filter === 'all') return true;
  return normalizeOrderStatus(order && order.status ? String(order.status) : '') === filter;
}

function matchesOrderQuery(order: OrderListItem, query: string): boolean {
  if (!query) return true;
  var q = String(query).trim().toLowerCase();
  if (!q) return true;
  var title = getOrderDisplayTitle(order).toLowerCase();
  var tasks = String(order && order.tasks ? order.tasks : '').toLowerCase();
  var id = String(order && order.id ? order.id : '').toLowerCase();
  return title.indexOf(q) !== -1 || tasks.indexOf(q) !== -1 || id.indexOf(q) !== -1;
}

function sortOrders(items: OrderListItem[], sortMode: OrderSort): OrderListItem[] {
  var list = Array.isArray(items) ? items.slice() : [];
  list.sort(function (a: OrderListItem, b: OrderListItem) {
    var ta = Number(a && a.createdAt ? a.createdAt : 0);
    var tb = Number(b && b.createdAt ? b.createdAt : 0);
    if (sortMode === 'oldest') return ta - tb;
    return tb - ta;
  });
  return list;
}

function initOrdersListClick(): void {
  var listEl = getEl('orders-list');
  if (!listEl) return;
  if (listEl.dataset.ordersClickInit === '1') return;
  listEl.dataset.ordersClickInit = '1';
  function suppressCardPress(card: Element | null): void {
    if (!card || !card.classList) return;
    card.classList.add('order-card-no-press');
    setTimeout(function () {
      try { card.classList.remove('order-card-no-press'); } catch (_) {}
    }, 360);
  }

  listEl.addEventListener('click', function (e: MouseEvent) {
    var infoBtn = closestEl(e.target, '.order-info-btn');
    if (infoBtn) {
      e.preventDefault();
      e.stopPropagation();
      suppressCardPress(closestEl(infoBtn, '.order-card, .order-spotlight, .order-row-compact'));
      var text = infoBtn.getAttribute('data-info') || '';
      if (text) showOrderPopover(text, infoBtn as HTMLElement);
      return;
    }
    var card = closestEl(e.target, '.order-card, .order-spotlight, .order-row-compact');
    if (!card) return;
    var oid = card.getAttribute('data-order-id') || '';
    if (!oid) return;
    var ouid = card.getAttribute('data-order-user-id') || '';
    var order: OrderListItem | null = null;
    for (var i = 0; i < ordersCache.length; i++) {
      if (String(ordersCache[i] && ordersCache[i].id) === String(oid)) {
        order = ordersCache[i];
        break;
      }
    }
    if (!order && getIsOwnerApp()) {
      for (var j = 0; j < ordersOwnerAllCache.length; j++) {
        if (String(ordersOwnerAllCache[j] && ordersOwnerAllCache[j].id) === String(oid)) {
          order = ordersOwnerAllCache[j];
          break;
        }
      }
    }
    if (!order) return;
    activeOrderUserId = ouid || (order && order.userId ? String(order.userId) : null);
    renderOrderDetail(order);
    showView('view-order-detail', 'right');
  });

  listEl.addEventListener('pointerdown', function (e: PointerEvent) {
    var infoBtn = closestEl(e.target, '.order-info-btn');
    if (!infoBtn) return;
    suppressCardPress(closestEl(infoBtn, '.order-card, .order-spotlight, .order-row-compact'));
  });

  listEl.addEventListener('keydown', function (e: KeyboardEvent) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var active = document.activeElement;
    if (!active) return;
    if (!active.classList) return;
    if (
      !active.classList.contains('order-card') &&
      !active.classList.contains('order-spotlight') &&
      !active.classList.contains('order-row-compact')
    ) {
      return;
    }
    e.preventDefault();
    if (active instanceof HTMLElement) active.click();
  });
}

function renderOrderCard(order: OrderListItem, index: number): string {
  var title = escapeAttr(getOrderDisplayTitle(order));
  var createdAt = order && order.createdAt ? formatOrderCreatedAt(order.createdAt) : '';
  var status = getOrderStatusUi(order && order.status ? String(order.status) : '');
  var preview = getOrderTasksPreview(order);
  var typeKey = getOrderTypeKey(order);
  var typeIcon = getOrderTypeIcon(typeKey);
  var progressBlock = '';
  if (isCustomBuildOrder(order)) {
    var progress = getOrderProgress(order);
    progressBlock =
      '<div class="order-card-progress">' +
      '<div class="order-card-progress-track">' +
      '<div class="order-card-progress-fill" style="width:' +
      String(progress.pct) +
      '%"></div></div>' +
      '<span class="order-card-progress-label">' +
      escapeAttr(progress.label) +
      '</span></div>';
  }
  var clientLine = '';
  if (getIsOwnerApp()) {
    var u1 = order && order.username_tg ? String(order.username_tg) : '';
    var u2 = order && order.username_form ? String(order.username_form) : '';
    var who = (u1 && u1.trim())
      ? ('@' + u1.replace(/^@/, ''))
      : (u2 && u2.trim() ? ('@' + u2.replace(/^@/, '')) : '@username не указан');
    clientLine = '<span class="order-card-client">' + escapeAttr(who) + '</span>';
  }
  var delay = Math.min(index * 0.05, 0.35).toFixed(2);

  return (
    '<article class="order-card order-card--' +
    escapeAttr(typeKey) +
    '" role="button" tabindex="0" style="animation-delay:' +
    delay +
    's" data-order-id="' +
    escapeAttr(order && order.id ? String(order.id) : '') +
    '" data-order-user-id="' +
    escapeAttr(order && order.userId ? String(order.userId) : '') +
    '">' +
    '<div class="order-card-glow" aria-hidden="true"></div>' +
    '<div class="order-card-inner">' +
    '<div class="order-card-top">' +
    '<span class="order-card-type" aria-hidden="true">' +
    typeIcon +
    '</span>' +
    '<div class="order-card-head">' +
    '<h3 class="order-card-title">' +
    title +
    '</h3>' +
    '<span class="order-status-wrap">' +
    '<span class="order-status-badge ' +
    escapeAttr(status.cls) +
    '">' +
    escapeAttr(status.label) +
    '</span>' +
    (!getIsOwnerApp() && status && status.info
      ? '<button type="button" class="order-info-btn" aria-label="Информация" data-info="' +
        escapeAttr(status.info) +
        '">i</button>'
      : '') +
    '</span></div>' +
    '<span class="order-card-chevron" aria-hidden="true">›</span></div>' +
    progressBlock +
    renderOrderCardSpecs(preview) +
    '<div class="order-card-foot">' +
    (createdAt ? '<time class="order-card-date">' + escapeAttr(createdAt) + '</time>' : '<span></span>') +
    clientLine +
    '</div></div></article>'
  );
}

function renderOrderDetail(order: OrderListItem): void {
  var titleEl = getEl('order-detail-title');
  var subtitleEl = getEl('order-detail-subtitle');
  var statusEl = getEl('order-detail-status');
  var statusInfoBtn = getEl<HTMLButtonElement>('order-detail-status-info');
  var idEl = getEl('order-detail-id');
  var ownerControls = getEl('order-owner-controls');
  var ownerSelect = getSelect('order-owner-status-select');
  var ownerConfirm = getEl<HTMLButtonElement>('order-owner-confirm-payment');
  var ownerSave = getEl<HTMLButtonElement>('order-owner-save-status');
  var ownerDelete = getEl<HTMLButtonElement>('order-owner-delete-order');
  var ownerHint = getEl('order-owner-save-hint');
  var tasksEl = getEl('order-detail-tasks');

  applyOrderDetailHeroTheme(order);
  if (titleEl) titleEl.textContent = getOrderDisplayTitle(order);
  if (subtitleEl) subtitleEl.textContent = getOrderSubtitle(order);
  if (tasksEl) tasksEl.innerHTML = renderOrderTasksHtml(order);
  if (idEl) {
    var idLine = '';
    if (order && order.createdAt) {
      var createdLine = formatOrderCreatedAt(order.createdAt);
      if (createdLine) idLine = 'Создан ' + createdLine;
    }
    if (order && order.id) {
      idLine = (idLine ? idLine + ' · ' : '') + '№ ' + String(order.id).slice(-8);
    }
    idEl.textContent = idLine;
  }
  renderOrderFlowTimeline(order);
  renderOrderQuoteBlock(order);
  renderOwnerQuotePanel(order);
  renderOrderVideoReportSection(order);
  renderOrderShipmentSection(order);
  renderOrderSupportActions(order);
  renderOrderOwnerFulfillment(order);
  if (statusEl) {
    var status = getOrderStatusUi(order && order.status ? String(order.status) : '');
    statusEl.className = 'order-status-badge ' + status.cls;
    statusEl.textContent = status.label;
  }
  if (statusInfoBtn) {
    var status2 = getOrderStatusUi(order && order.status ? String(order.status) : '');
    statusInfoBtn.style.display = (!getIsOwnerApp() && status2 && status2.info) ? '' : 'none';
    statusInfoBtn.onclick = function (e: MouseEvent) {
      e.preventDefault();
      e.stopPropagation();
      if (!getIsOwnerApp() && status2 && status2.info) showOrderPopover(status2.info, statusInfoBtn);
    };
  }
  if (ownerControls) {
    if (getIsOwnerApp()) ownerControls.style.display = '';
    else ownerControls.style.display = 'none';
  }
  if (ownerHint) {
    ownerHint.style.display = 'none';
    ownerHint.textContent = '';
  }
  if (getIsOwnerApp() && ownerSelect) {
    ownerSelect.value = normalizeOrderStatus(order && order.status ? String(order.status) : '');
  }
  if (ownerConfirm) {
    const confirmBtn = ownerConfirm;
    const st = normalizeOrderStatus(order && order.status ? String(order.status) : '');
    const showConfirm = getIsOwnerApp() && (st === 'awaiting_payment' || st === 'payment_pending');
    confirmBtn.hidden = !showConfirm;
    if (showConfirm) {
      confirmBtn.onclick = async function () {
        const uid = activeOrderUserId || (order && order.userId ? String(order.userId) : '');
        const oid = order && order.id ? String(order.id) : '';
        if (!uid || !oid) return;
        confirmBtn.disabled = true;
        try {
          const data = await apiFetchJSON<OrdersApiResponse>('/api/owner/orders/confirm-payment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: uid, orderId: oid })
          });
          if (data?.ok) {
            const saved = data.item && typeof data.item === 'object' ? data.item : null;
            const nextStatus = saved && saved.status ? String(saved.status) : 'building';
            const nextOrder = { ...order, ...(saved || {}), status: nextStatus };
            const confirmedId = order && order.id ? String(order.id) : '';
            [ordersCache, ordersOwnerAllCache].forEach(function (list) {
              for (var i = 0; i < list.length; i++) {
                if (String(list[i] && list[i].id) === confirmedId) {
                  list[i] = { ...list[i], ...nextOrder };
                }
              }
            });
            renderOrderDetail(nextOrder);
            renderOrdersListState();
            if (ownerHint) {
              ownerHint.style.display = '';
              ownerHint.textContent = 'Оплата подтверждена.';
            }
          }
        } catch {
          /* ignore */
        } finally {
          confirmBtn.disabled = false;
        }
      };
    }
  }
  if (getIsOwnerApp() && ownerSave) {
    const saveBtn = ownerSave;
    saveBtn.onclick = async function () {
      try {
        if (!ownerSelect) return;
        saveBtn.disabled = true;
        saveBtn.textContent = 'Сохранение...';
        var st = String(ownerSelect.value || '');
        var uid = activeOrderUserId || (order && order.userId ? String(order.userId) : '');
        var oid = order && order.id ? String(order.id) : '';
        if (!uid || !oid) return;

        var data = await apiFetchJSON<OrdersApiResponse>('/api/owner/orders/update-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: uid, orderId: oid, status: st })
        }).catch(function () { return null; });
        if (data && data.ok) {
          // обновляем локальные кэши, чтобы UI сразу поменялся
          var nextStatus = st;
          for (var i = 0; i < ordersOwnerAllCache.length; i++) {
            if (String(ordersOwnerAllCache[i] && ordersOwnerAllCache[i].id) === oid) {
              ordersOwnerAllCache[i].status = nextStatus;
              break;
            }
          }
          for (var j = 0; j < ordersCache.length; j++) {
            if (String(ordersCache[j] && ordersCache[j].id) === oid) {
              ordersCache[j].status = nextStatus;
              break;
            }
          }
          if (ownerHint) {
            ownerHint.style.display = '';
            ownerHint.textContent = 'Статус обновлён.';
          }
          renderOrderDetail({ ...order, status: nextStatus });
          renderOrdersListState();
        } else {
          if (ownerHint) {
            ownerHint.style.display = '';
            ownerHint.textContent = 'Не удалось обновить статус.';
          }
        }
      } catch (_) {
        if (ownerHint) {
          ownerHint.style.display = '';
          ownerHint.textContent = 'Ошибка сети при обновлении статуса.';
        }
      }
      saveBtn.disabled = false;
      saveBtn.textContent = 'Сохранить статус';
    };
  }
  if (getIsOwnerApp() && ownerDelete) {
    const deleteBtn = ownerDelete;
    deleteBtn.onclick = async function () {
      try {
        var uid = (order && order.userId ? String(order.userId) : '') || activeOrderUserId || '';
        var oid = order && order.id ? String(order.id) : '';
        if (!uid || !oid) return;

        var confirmed = await confirmOwnerAction(
          'Удалить заказ?',
          'Заказ будет удалён без возможности восстановления.',
          'Удалить',
          true
        );
        if (!confirmed) return;

        deleteBtn.disabled = true;
        deleteBtn.textContent = 'Удаление...';
        var data = await apiFetchJSON<OrdersApiResponse>('/api/owner/orders/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: uid, orderId: oid })
        }).catch(function () { return null; });
        if (data && data.ok) {
          ordersOwnerAllCache = ordersOwnerAllCache.filter(function (o: OrderListItem) { return String(o && o.id) !== String(oid); });
          ordersCache = ordersCache.filter(function (o: OrderListItem) { return String(o && o.id) !== String(oid); });
          renderOrdersListState();
          showView('view-profile', 'left');
        } else {
          if (ownerHint) {
            ownerHint.style.display = '';
            ownerHint.textContent = 'Не удалось удалить заказ.';
          }
        }
      } catch (_) {
        if (ownerHint) {
          ownerHint.style.display = '';
          ownerHint.textContent = 'Ошибка сети при удалении заказа.';
        }
      }
      deleteBtn.disabled = false;
      deleteBtn.textContent = 'Удалить заказ';
    };
  }

}

function updateOrdersPageLead(totalCount: number): void {
  var leadEl = getEl('orders-page-lead');
  if (!leadEl) return;
  if (ordersLoading) {
    leadEl.textContent = '…';
    return;
  }
  leadEl.textContent = formatOrdersCount(totalCount);
}

function renderOrdersListState(): void {
  var listEl = getEl('orders-list');
  var emptyEl = getEl('orders-empty');
  var summaryEl = getEl('orders-summary');
  if (!listEl || !emptyEl) return;

  var source = getIsOwnerApp() ? ordersOwnerAllCache : ordersCache;
  if (summaryEl) summaryEl.textContent = computeOrdersSummary(source);

  var filtered: OrderListItem[] = [];
  for (var i = 0; i < source.length; i++) {
    var item = source[i];
    if (!matchesOrderFilter(item, ordersUiState.filter)) continue;
    if (!matchesOrderQuery(item, ordersUiState.query)) continue;
    filtered.push(item);
  }
  filtered = sortOrders(filtered, ordersUiState.sort);

  updateOrdersPageLead(source.length);

  if (!filtered.length) {
    listEl.innerHTML = '';
    if (ordersLoading) {
      emptyEl.style.display = 'none';
      return;
    }
    emptyEl.style.display = '';
    return;
  }

  emptyEl.style.display = 'none';

  if (!getIsOwnerApp()) {
    if (filtered.length === 1) {
      listEl.innerHTML = renderOrderSpotlight(filtered[0]);
      return;
    }
    var spotlight = renderOrderSpotlight(filtered[0]);
    var more =
      '<p class="orders-more-label">Другие заказы</p>' +
      filtered
        .slice(1)
        .map(function (order) {
          return renderOrderRowCompact(order);
        })
        .join('');
    listEl.innerHTML = spotlight + more;
    return;
  }

  listEl.innerHTML = filtered.map(function (order, idx) { return renderOrderCard(order, idx); }).join('');
}

function initOrdersControls(): void {
  var filtersEl = getEl('orders-filters');
  var searchEl = getInput('orders-search');
  var sortEl = getSelect('orders-sort');
  var sortWrap = sortEl ? sortEl.closest('.orders-sort-wrap') : null;
  var refreshBtn = getEl<HTMLButtonElement>('orders-refresh-btn');
  var summaryEl = getEl('orders-summary');
  var panelEl: HTMLElement | null = summaryEl ? summaryEl.closest('.orders-panel') as HTMLElement | null : document.querySelector('.orders-panel');

  // Для обычных пользователей полностью убираем панель управления заказами
  if (panelEl) {
    panelEl.style.display = getIsOwnerApp() ? '' : 'none';
  }

  if (searchEl) {
    // поиск только для владельца
    searchEl.style.display = getIsOwnerApp() ? '' : 'none';
  }
  if (sortWrap instanceof HTMLElement) {
    // сортировка только для владельца
    sortWrap.style.display = getIsOwnerApp() ? '' : 'none';
  }
  if (filtersEl && filtersEl.dataset.ordersFiltersInit !== '1') {
    const filters = filtersEl;
    filters.dataset.ordersFiltersInit = '1';
    filters.addEventListener('click', function (e: MouseEvent) {
      var btn = closestEl(e.target, '[data-order-filter]');
      if (!btn) return;
      var next = (btn.getAttribute('data-order-filter') || 'all') as OrderFilter;
      ordersUiState.filter = next;
      var chips = filters.querySelectorAll('[data-order-filter]');
      for (var i = 0; i < chips.length; i++) {
        if (chips[i] === btn) chips[i].classList.add('orders-filter-chip-active');
        else chips[i].classList.remove('orders-filter-chip-active');
      }
      renderOrdersListState();
    });
  }
  if (searchEl && searchEl.dataset.ordersSearchInit !== '1') {
    const searchInput = searchEl;
    searchInput.dataset.ordersSearchInit = '1';
    searchInput.addEventListener('input', function () {
      ordersUiState.query = String(searchInput.value || '').trim();
      renderOrdersListState();
    });
  }
  if (sortEl && sortEl.dataset.ordersSortInit !== '1') {
    const sortSelect = sortEl;
    sortSelect.dataset.ordersSortInit = '1';
    sortSelect.addEventListener('change', function () {
      ordersUiState.sort = (String(sortSelect.value || 'newest') as OrderSort);
      renderOrdersListState();
    });
  }
  if (refreshBtn && refreshBtn.dataset.ordersRefreshInit !== '1') {
    refreshBtn.dataset.ordersRefreshInit = '1';
    refreshBtn.addEventListener('click', function () {
      loadMyOrders();
    });
  }

  var emptyCta = getEl<HTMLButtonElement>('orders-empty-cta');
  if (emptyCta && emptyCta.dataset.ordersEmptyCtaInit !== '1') {
    emptyCta.dataset.ordersEmptyCtaInit = '1';
    emptyCta.addEventListener('click', function () {
      setActiveTab(document.getElementById('tab-order'));
      showView('view-main', 'left');
    });
  }
}

export function prepareOrdersListLoadingUi(): void {
  ordersLoading = true;
  var listEl = getEl('orders-list');
  var emptyEl = getEl('orders-empty');
  var loadingEl = getEl('orders-loading');
  var leadEl = getEl('orders-page-lead');
  if (emptyEl) emptyEl.style.display = 'none';
  if (listEl) listEl.innerHTML = '';
  if (loadingEl) {
    loadingEl.style.display = 'flex';
    loadingEl.setAttribute('aria-busy', 'true');
  }
  if (leadEl) leadEl.textContent = '…';
}

function finishOrdersLoading(seq: number): void {
  if (seq !== ordersLoadSeq) return;
  ordersLoading = false;
  var loadingEl = getEl('orders-loading');
  if (loadingEl) {
    loadingEl.style.display = 'none';
    loadingEl.setAttribute('aria-busy', 'false');
  }
}

export async function loadMyOrders(): Promise<void> {
  var listEl = getEl('orders-list');
  var emptyEl = getEl('orders-empty');
  if (!listEl || !emptyEl) return;
  initOrdersControls();

  var seq = ++ordersLoadSeq;
  prepareOrdersListLoadingUi();

  if (getIsOwnerApp()) {
    try {
      var dataOwner = await apiFetchJSON<OrdersApiResponse>('/api/owner/orders', { method: 'GET' }).catch(function () { return null; });
      if (seq !== ordersLoadSeq) return;
      var itemsOwner = dataOwner && dataOwner.ok && Array.isArray(dataOwner.items) ? dataOwner.items : [];
      ordersOwnerAllCache = itemsOwner;
      finishOrdersLoading(seq);
      initOrdersListClick();
      renderOrdersListState();
    } catch (_) {
      if (seq !== ordersLoadSeq) return;
      finishOrdersLoading(seq);
      emptyEl.style.display = '';
      listEl.innerHTML = '';
      var summaryOwnerError = getEl('orders-summary');
      if (summaryOwnerError) summaryOwnerError.textContent = 'Не удалось обновить заказы. Попробуйте снова.';
    }
    return;
  }

  if (!getTelegramInitData()) {
    if (seq !== ordersLoadSeq) return;
    finishOrdersLoading(seq);
    emptyEl.style.display = '';
    listEl.innerHTML = '';
    var summaryNoUser = getEl('orders-summary');
    if (summaryNoUser) summaryNoUser.textContent = 'Авторизуйтесь в Telegram, чтобы увидеть историю заказов.';
    return;
  }

  try {
    var data = await apiFetchJSON<OrdersApiResponse>('/api/my-orders', { method: 'GET' }).catch(function () { return null; });
    if (seq !== ordersLoadSeq) return;
    var items = data && data.ok && Array.isArray(data.items) ? data.items : [];

    ordersCache = items;
    finishOrdersLoading(seq);
    initOrdersListClick();
    renderOrdersListState();
  } catch (_) {
    if (seq !== ordersLoadSeq) return;
    finishOrdersLoading(seq);
    emptyEl.style.display = '';
    listEl.innerHTML = '';
    var summaryError = getEl('orders-summary');
    if (summaryError) summaryError.textContent = 'Не удалось обновить заказы. Попробуйте снова.';
  }
}
